import type { LibraryRow } from '@/state/experiments/library-learnings';
import type { UseDataControlReturn } from '@/components/DataTable';
import type { ExperimentCounts } from '@/state/experiments/useExperimentsList';
import { useState } from 'react';
import { CreateExperimentModal } from './CreateExperimentModal';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { SearchInput } from '@/components/SearchInput';
import { cn } from 'helpers/cn';
import { FOCUS_RING, SEARCH_FIELD_CLASS } from '../../ConsolePanel';
import { useExperimentsList } from '@/state/experiments/useExperimentsList';
import { LearningsFilter } from './LearningsFilter';

type TopHeaderProps = {
  manager: ReturnType<typeof useExperimentsList>;
  dataController: UseDataControlReturn<LibraryRow>;
};

/**
 * One toolbar, not two rows.
 *
 * The views, the search and the create button all act on the same table, so they sit
 * on a single strip: filters left, then the two controls pushed right. The old layout
 * built the whole thing twice — a desktop copy and a mobile copy positioned absolutely
 * over each other — which is what made the search land on a second row. A single
 * wrapping row covers both widths, and `SearchInput` already collapses to a magnifier
 * on small screens, so nothing needs a breakpoint here.
 */
export const TopHeader = (props: TopHeaderProps) => {
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const { searchQuery, setSearchQuery, setSortedBy, changePage } = props.dataController;
  const { VIEWS, view, setView, counts } = props.manager;
  /*
    The count is appended only once there is one. `counts` is null until the query
    resolves — and stays null while no website is picked, since the query never runs —
    so interpolating it unconditionally made the filter strip read "All (undefined)",
    "Running (undefined)" on every load.
  */
  const filters = VIEWS.map((v) => ({
    value: v,
    label: counts ? `${v} (${counts[v as keyof ExperimentCounts]})` : v,
  }));

  const resetFilters = () => {
    setSearchQuery('');
    setView('All');
    props.manager.clearLearningFilters();
    setSortedBy(null);
    changePage(1);
  };

  return (
    <>
      {/* Below `md` the three groups are stacked rows rather than one wrapping line:
          at 375px the pill strip alone fills the width, so the search and the create
          button each get their own full-width row underneath it. */}
      <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center">
        {/* Pills rather than the shared segmented Tabs: the console reads filters as
            chips, and the row has to survive a narrow viewport, so it scrolls sideways
            instead of collapsing into a select. */}
        <div
          className="-mx-1 flex min-w-0 items-center gap-1.5 overflow-x-auto px-1 py-0.5"
          data-tour="experiments-views"
        >
          {/* No status views on the timeline: it always draws every experiment, so a pill
              there would claim a filter that is not applied. */}
          {props.manager.layout === 'timeline' ? (
            <span className="supporting px-1">Every experiment, archived included</span>
          ) : (
            filters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setView(filter.value)}
                className={cn(
                  // border-input on the unselected pill: it carries no fill, so the
                  // outline is the only thing drawing it, and --line is 10% alpha.
                  // min-h-9 clears the 36px touch floor on a phone.
                  'micro-label rounded-pill flex min-h-9 shrink-0 items-center border px-3 py-1.5',
                  'transition-colors duration-150 md:min-h-0',
                  FOCUS_RING,
                  filter.value === view
                    ? 'bg-sel-bg border-sel-line text-foreground'
                    : 'border-input text-dim hover:bg-hover hover:text-foreground',
                )}
              >
                {filter.label}
              </button>
            ))
          )}
          <button
            type="button"
            onClick={resetFilters}
            className={cn(
              'micro-label rounded-pill border-input text-dim hover:bg-hover hover:text-foreground',
              'flex min-h-9 shrink-0 items-center border px-3 py-1.5 transition-colors duration-150 md:min-h-0',
              FOCUS_RING,
            )}
          >
            Reset
          </button>
        </div>

        <div className="flex flex-col gap-2 md:ml-auto md:shrink-0 md:flex-row md:items-center">
          {/* One set of experiments, two layouts: the filters and search on this strip
              apply to both, which is why this is a toggle and not another page. */}
          <div
            className="border-input flex shrink-0 rounded-lg border p-0.5"
            role="group"
            aria-label="Layout"
            data-tour="experiments-layout"
          >
            {(['table', 'timeline'] as const).map((layout) => (
              <button
                key={layout}
                type="button"
                aria-pressed={props.manager.layout === layout}
                onClick={() => props.manager.setLayout(layout)}
                className={cn(
                  'micro-label min-h-8 flex-1 rounded-md px-3 capitalize transition-colors duration-150',
                  FOCUS_RING,
                  props.manager.layout === layout ? 'bg-sel-bg text-foreground' : 'text-dim hover:text-foreground',
                )}
              >
                {layout}
              </button>
            ))}
          </div>
          {/* `relative`, and full width on a phone: SearchInput collapses to a
              magnifier below `md` and drops its field in absolutely, so it needs a
              positioned parent of the width the field should take. */}
          {
            <>
              <LearningsFilter manager={props.manager} />
              <div className="relative w-full md:w-auto" data-tour="experiments-search">
                <SearchInput
                  value={searchQuery}
                  handleSearch={setSearchQuery}
                  placeholder="Search names, hypotheses, notes"
                  className={SEARCH_FIELD_CLASS}
                />
              </div>
            </>
          }
          <Button
            onClick={() => setIsCreating(true)}
            className="h-9 w-full shrink-0 rounded-lg md:w-auto"
            data-tour="experiments-new"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create experiment
          </Button>
        </div>
      </div>
      {isCreating && <CreateExperimentModal isCreating={isCreating} setIsCreating={setIsCreating} />}
    </>
  );
};
